import React, { useMemo } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { NumberCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer/NumberCellRenderer'
import CustomTooltip from '../../../components/tooltips/Tooltip/CustomTooltip'
import {
  getVarianceInfo,
  getVarianceTargetRow,
  SprintEstimatedHourInfo,
} from '../gridOptions/varianceUtils'
import { intl } from '../../../../i18n'
import Workload, {
  getWorkloadUnitValue,
  WorkloadUnit,
} from '../../../../lib/functions/workload'

// 工数単位のラベルを取得
const getWorkloadUnitLabel = (workloadUnit: WorkloadUnit): string => {
  switch (workloadUnit) {
    case WorkloadUnit.MONTH:
      return intl.formatMessage({ id: 'workloadUnit.month' })
    case WorkloadUnit.DAY:
      return intl.formatMessage({ id: 'workloadUnit.day' })
    case WorkloadUnit.HOUR:
    default:
      return intl.formatMessage({ id: 'workloadUnit.hour' })
  }
}

// 時間を選択中の単位に変換
const convertHourToUnit = (
  hour: number,
  workloadUnit: WorkloadUnit,
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number }
): number => {
  const workload = Workload.from({
    hour,
    standard: workloadStandard,
  })
  return getWorkloadUnitValue(workload, workloadUnit)
}

// スプリント内訳を文字列に変換
const formatSprintBreakdown = (
  sprintBreakdown: SprintEstimatedHourInfo[],
  workloadUnit: WorkloadUnit,
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number },
  unitLabel: string
): string => {
  if (sprintBreakdown.length === 0) return ''

  return sprintBreakdown
    .map(sprint => {
      const value = convertHourToUnit(
        sprint.estimatedHour,
        workloadUnit,
        workloadStandard
      )
      return `  ${sprint.sprintName}: ${value}${unitLabel}`
    })
    .join('\n')
}

const EstimatedWorkloadCellRenderer: React.FC<ICellRendererParams> = props => {
  const tooltipValue = useMemo((): string => {
    const row = getVarianceTargetRow(props.data)
    if (!row) return ''
    const { originalHour, sprintTotalHour, hasVariance, sprintBreakdown } =
      getVarianceInfo(row)
    if (!hasVariance) return ''

    // contextから工数単位の設定を取得
    const workloadUnit =
      props.context?.workloadUnitState?.selectedWorkloadUnit ||
      WorkloadUnit.HOUR
    const workloadStandard = props.context?.workloadUnitState || {
      dailyWorkHours: 1,
      monthlyWorkDays: 1,
    }
    const unitLabel = getWorkloadUnitLabel(workloadUnit)

    // 時間を選択中の単位に変換
    const originalValue = convertHourToUnit(
      originalHour,
      workloadUnit,
      workloadStandard
    )
    const sprintTotalValue = convertHourToUnit(
      sprintTotalHour,
      workloadUnit,
      workloadStandard
    )

    // スプリント内訳を生成
    const breakdownText = formatSprintBreakdown(
      sprintBreakdown,
      workloadUnit,
      workloadStandard,
      unitLabel
    )

    return intl.formatMessage(
      { id: 'productBacklog.estimatedWorkload.variance.tooltip' },
      {
        original: originalValue.toString(),
        sprintTotal: sprintTotalValue.toString(),
        unit: unitLabel,
        breakdown: breakdownText,
      }
    )
  }, [props.data, props.context?.workloadUnitState, intl])

  // 乖離がない場合は元のNumberCellRendererをそのまま使用
  if (!tooltipValue) {
    return <NumberCellRenderer {...props} />
  }

  return (
    <CustomTooltip value={tooltipValue}>
      <span>
        <NumberCellRenderer {...props} />
      </span>
    </CustomTooltip>
  )
}

export default EstimatedWorkloadCellRenderer
